import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { Camera, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Child profile photo with inline upload (parent or admin). */
export const ChildPhotoAvatar = ({ playerId, name, size = 48, editable = true }) => {
  const [version, setVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/media/player/${playerId}/photo`, form);
      setVersion((v) => v + 1);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload the photo");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <AuthImage
        key={version}
        src={`${API}/media/player/${playerId}/photo`}
        alt={name}
        className="w-full h-full rounded-full object-cover border border-slate-700"
        testId={`child-photo-${playerId}`}
        fallback={
          <div className="w-full h-full rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-semibold text-slate-400">
            {initials}
          </div>
        }
      />
      {editable && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`child-photo-input-${playerId}`}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-cyan-600 hover:bg-cyan-500 flex items-center justify-center border border-slate-900 transition-colors"
            title="Change photo"
            data-testid={`child-photo-upload-${playerId}`}
          >
            {uploading ? (
              <Loader2 className="w-3 h-3 text-white animate-spin" />
            ) : (
              <Camera className="w-3 h-3 text-white" />
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default ChildPhotoAvatar;
